import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { authService } from '@/services/auth'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { Card } from '@/components/ui/Card'
import { Smartphone, Mail, CheckCircle, ArrowLeft } from 'lucide-react'

export const Verify: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  useAuthStore()

  const [email, setEmail] = useState('')
  const [verificationStep, setVerificationStep] = useState<'mobile' | 'email'>('mobile')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [mobileVerified, setMobileVerified] = useState(false)
  const [registrationComplete, setRegistrationComplete] = useState(false)

  useEffect(() => {
    const state = location.state as { email?: string; step?: string }
    if (state?.email) {
      setEmail(state.email)
    } else {
      navigate('/register')
    }
    if (state?.step === 'email') {
      setVerificationStep('email')
      setMobileVerified(true)
    }
  }, [location, navigate])

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Please enter the 6-digit code')
      return
    }

    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      if (verificationStep === 'mobile') {
        await authService.verifyMobile({ email, code, verification_type: 'mobile' })
        setMobileVerified(true)
        setSuccess('Mobile number verified successfully!')
        setVerificationStep('email')
        setCode('')
        setResendCooldown(0)
      } else {
        await authService.verifyEmail({ email, code, verification_type: 'email' })
        setRegistrationComplete(true)
      }
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await authService.resendVerification(email, verificationStep)
      setSuccess(`A new verification code has been sent to your ${verificationStep === 'mobile' ? 'mobile' : 'email'}.`)
      setResendCooldown(60)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const stepConfig = {
    mobile: {
      icon: <Smartphone className="w-8 h-8 text-primary-600" />,
      step: 1,
      title: 'Verify Your Mobile Number',
      description: 'Please enter the verification code sent to your mobile device via SMS.',
      inputLabel: 'Mobile Verification Code',
      buttonLabel: 'Verify Mobile',
      resendLabel: 'Resend SMS code',
    },
    email: {
      icon: <Mail className="w-8 h-8 text-primary-600" />,
      step: 2,
      title: 'Verify Your Email Address',
      description: 'Almost done! Please enter the verification code sent to your email address. (If you cannot see it, check your Spam Folder)',
      inputLabel: 'Email Verification Code',
      buttonLabel: 'Verify Email & Complete',
      resendLabel: 'Resend email code',
    },
  }

  const cfg = stepConfig[verificationStep]

  if (registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <Card className="text-center p-8">
            <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-9 h-9 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful! 🎉</h2>
            <p className="text-gray-600 mb-1">
              Thank you for registering with Silux Mobile — your account is verified and ready to go.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You can now sign in with your email and password to start exploring your dashboard.
            </p>
            <Button fullWidth onClick={() => navigate('/login')}>
              Continue to Sign In
            </Button>
            <button
              onClick={() => navigate('/')}
              className="mt-3 text-sm text-gray-500 hover:text-primary-600 transition-colors"
            >
              Back to home
            </button>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">

        {/* Header */}
        <div className="relative text-center mb-6">
          <button
            onClick={() => navigate('/register')}
            className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-1 text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Verify Your Account</h2>
          <p className="mt-1 text-sm text-gray-500">Step {cfg.step} of 2</p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {/* Step 1 — Mobile */}
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
              mobileVerified
                ? 'bg-primary-600 text-white'
                : verificationStep === 'mobile'
                ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                : 'bg-gray-200 text-gray-500'
            }`}>
              {mobileVerified ? <CheckCircle className="w-5 h-5" /> : '1'}
            </div>
            <span className={`text-xs mt-1 font-medium ${verificationStep === 'mobile' ? 'text-primary-600' : mobileVerified ? 'text-primary-600' : 'text-gray-400'}`}>
              Mobile
            </span>
          </div>

          <div className={`h-0.5 w-16 mb-4 transition-colors ${mobileVerified ? 'bg-primary-600' : 'bg-gray-200'}`} />

          {/* Step 2 — Email */}
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
              verificationStep === 'email'
                ? 'bg-primary-600 text-white ring-4 ring-primary-100'
                : 'bg-gray-200 text-gray-500'
            }`}>
              2
            </div>
            <span className={`text-xs mt-1 font-medium ${verificationStep === 'email' ? 'text-primary-600' : 'text-gray-400'}`}>
              Email
            </span>
          </div>
        </div>

        <Card>
          {error && (
            <Alert variant="error" message={error} onClose={() => setError(null)} className="mb-4" />
          )}
          {success && (
            <Alert variant="success" message={success} onClose={() => setSuccess(null)} className="mb-4" />
          )}

          <div className="space-y-5">
            {/* Icon + description */}
            <div className="flex items-start gap-4 bg-primary-50 rounded-xl p-4">
              <div className="flex-shrink-0 mt-0.5">{cfg.icon}</div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">{cfg.title}</p>
                <p className="text-sm text-gray-600 mt-1">{cfg.description}</p>
                {verificationStep === 'mobile' && (
                  <p className="text-xs text-gray-500 mt-1">Sending to: <strong>{email.replace(/(.{2}).*@/, '$1***@')}</strong>'s registered mobile</p>
                )}
                {verificationStep === 'email' && (
                  <p className="text-xs text-gray-500 mt-1">Sending to: <strong>{email}</strong></p>
                )}
              </div>
            </div>

            <Input
              label={cfg.inputLabel}
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              maxLength={6}
            />

            <Button
              onClick={handleVerify}
              fullWidth
              isLoading={isLoading}
              disabled={code.length !== 6 || isLoading}
            >
              {cfg.buttonLabel}
            </Button>

            <div className="text-center space-y-1">
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0 || isLoading}
                className="text-sm text-primary-600 hover:text-primary-700 disabled:text-gray-400 font-medium"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : cfg.resendLabel}
              </button>
              <p className="text-xs text-gray-400">Code expires in 10 minutes &bull; Max 5 attempts</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default Verify
